import { useEffect, useState } from 'react';

/**
 * Chart-Farben aus den Design-Tokens (AGE-160).
 *
 * Chart.js zeichnet auf ein Canvas und kann `var(--color-x)` nicht aufloesen —
 * die Tokens muessen zur Laufzeit aus dem CSS ausgelesen werden. Vorher standen
 * die Farben als Hex-Literale in den Chart-Komponenten und folgten damit weder
 * Whitelabel (`packages/ui/src/whitelabel.ts`) noch den `.dark`-Overrides aus
 * `packages/ui/styles/tokens.css`.
 *
 * Die Fallbacks entsprechen den Token-Werten und greifen nur im SSR/Test, wo
 * kein `document` existiert.
 */

/** Liest eine CSS-Custom-Property von `<html>`; `var()`-Ketten loest der Browser auf. */
function readToken(name: string, fallback: string): string {
  if (typeof document === 'undefined') return fallback;

  const value = getComputedStyle(document.documentElement)
    .getPropertyValue(name)
    .trim();

  return value || fallback;
}

/** Hex-Token mit Alpha versehen, fuer Flaechen unter Linien und Legenden-Kacheln. */
export function withAlpha(hex: string, alpha: number): string {
  const match = /^#?([0-9a-f]{6})$/i.exec(hex.trim());
  if (!match) return hex;

  const int = parseInt(match[1], 16);

  // eslint-disable-next-line no-bitwise
  return `rgba(${(int >> 16) & 255}, ${(int >> 8) & 255}, ${
    int & 255
  }, ${alpha})`;
}

export type ChartTheme = {
  /** Geordnete kategoriale Serienfarben (--color-chart-1..3). */
  readonly series: readonly string[];
  /** Flaechenfarbe zur ersten Serienfarbe, 15 % Deckkraft. */
  readonly seriesFill: string;
  readonly grid: string;
  readonly tick: string;
  readonly axisTitle: string;
  /**
   * Beschriftung *auf* der Balkenflaeche. Hintergrund ist dort die Serienfarbe
   * und nicht die Seitenflaeche — deshalb in beiden Modi gleich.
   */
  readonly datalabel: string;
  readonly tooltip: {
    readonly backgroundColor: string;
    readonly titleColor: string;
    readonly bodyColor: string;
    readonly borderColor: string;
  };
};

export function getChartTheme(isDark: boolean): ChartTheme {
  const series = [
    readToken('--color-chart-1', '#468cd3'),
    readToken('--color-chart-2', '#9ae272'),
    readToken('--color-chart-3', '#55d5c6'),
  ];

  // Die Graustufen sind in beiden Modi identisch (tokens.css ueberschreibt im
  // `.dark`-Block nur die Primary-Skala). Der Modus entscheidet also nur,
  // welcher Token-Schritt gezogen wird — nicht welcher Hex-Wert.
  const gray = (step: number, fallback: string) =>
    readToken(`--color-gray-${step}`, fallback);

  return {
    series,
    seriesFill: withAlpha(series[0], 0.15),
    grid: isDark ? gray(700, '#454440') : gray(100, '#e4e4e3'),
    tick: isDark ? gray(400, '#84837d') : gray(600, '#53534e'),
    axisTitle: isDark ? gray(200, '#c9c9c6') : gray(700, '#454440'),
    datalabel: '#ffffff',
    tooltip: {
      backgroundColor: isDark ? gray(800, '#393936') : '#ffffff',
      titleColor: isDark ? gray(50, '#f7f7f6') : gray(950, '#1d1d1b'),
      bodyColor: isDark ? gray(200, '#c9c9c6') : gray(700, '#454440'),
      borderColor: isDark ? gray(700, '#454440') : gray(100, '#e4e4e3'),
    },
  };
}

/**
 * Chart-Theme als Hook. Beobachtet die `class`-Attribute von `<html>` und
 * rechnet beim Dark-Mode-Umschalten neu — vorher lasen die Charts `isDark`
 * einmalig beim Rendern und blieben beim Themewechsel in den alten Farben.
 */
export function useChartTheme(): ChartTheme {
  const [
    isDark,
    setIsDark,
  ] = useState(
    () =>
      typeof document !== 'undefined' &&
      document.documentElement.classList.contains('dark'),
  );

  useEffect(() => {
    const element = document.documentElement;

    const sync = () => setIsDark(element.classList.contains('dark'));

    sync();

    const observer = new MutationObserver(sync);
    observer.observe(element, {
      attributes: true,
      attributeFilter: ['class'],
    });

    return () => observer.disconnect();
  }, []);

  return getChartTheme(isDark);
}
